import { Recipe } from "./Recipe";

export enum LinkableEntityType {
    User = "user",
    Post = "post",
    Recipe = "recipe",
    Workout = "workout",
    Routine = "routine",
}

export enum ContentEntityType {
    Post = "post",
    Recipe = "recipe",
    Workout = "workout",
    Routine = "routine",
    Image = "image",
}

export enum PresentationMode {
    Feed = "feed",
    Article = "article",
    ResourceShare = "resourceShare",
}

export enum SourceTextField {
    Title = "title",
    Content = "content",
    Comment = "comment",
    Description = "description",
    Summary = "summary",
    Caption = "caption",
}

export enum AccessVisibility {
    Public = "public",
    Private = "private",
    Unlisted = "unlisted",
}

export enum AccessMode {
    Free = "free",
    SubscriberOnly = "subscriberOnly",
    Paid = "paid",
    AdUnlock = "adUnlock",
    Hybrid = "hybrid",
}

export interface AccessPrice {
    amountCents: number;
    currency: "USD";
}

export interface AdUnlockConfig {
    enabled: boolean;
    durationHours: number;
    adsRequired: number;
}

export interface AccessModel {
    visibility: AccessVisibility;
    accessMode: AccessMode;
    ownerId?: string;
    price?: AccessPrice;
    adUnlock?: AdUnlockConfig;
    revenueShareEnabled?: boolean;
}

export enum AccessState {
    Viewable = "viewable",
    Locked = "locked",
    Private = "private",
    NotFound = "notFound",
}

export enum AccessDecisionReason {
    Free = "free",
    Owner = "owner",
    Admin = "admin",
    ActiveEntitlement = "activeEntitlement",
    ActiveCreatorSubscription = "activeCreatorSubscription",
    MissingEntitlement = "missingEntitlement",
    Private = "private",
    Unpublished = "unpublished",
    Blocked = "blocked",
    NotFound = "notFound",
    ProviderDisabled = "providerDisabled",
}

export interface UnlockOptions {
    subscribe: boolean;
    purchase: boolean;
    adUnlock: boolean;
}

export interface AccessDecision {
    accessState: AccessState;
    reason: AccessDecisionReason;
    visibility?: AccessVisibility;
    accessMode?: AccessMode;
    unlockOptions: UnlockOptions;
}

export interface EntityReference {
    entityType: LinkableEntityType;
    entityId: string;
    label: string;
    displayName: string;
    source: SourceTextField;
    startIndex?: number;
    endIndex?: number;
    snapshot?: {
        imageUrl?: string;
        subtitle?: string;
        ownerId?: string;
        visibility?: AccessVisibility;
        accessMode?: AccessMode;
    };
}

export interface TopicTagRef {
    slug: string;
    label: string;
    source: SourceTextField;
    startIndex?: number;
    endIndex?: number;
}

export enum EntityAttachmentDisplayMode {
    CompactCard = "compactCard",
    LargeCard = "largeCard",
    HeroCard = "heroCard",
    InlineImage = "inlineImage",
}

export interface EntityAttachment {
    entityType: ContentEntityType;
    entityId: string;
    displayMode: EntityAttachmentDisplayMode;
    sortOrder: number;
    snapshot?: {
        title?: string;
        subtitle?: string;
        imageUrl?: string;
        ownerId?: string;
    };
}

export interface LinkableEntityPreview {
    entityType: LinkableEntityType;
    entityId: string;
    displayName: string;
    subtitle?: string;
    imageUrl?: string;
    ownerId?: string;
    access?: AccessDecision;
}

export interface ContentReadResponse<T> {
    access: AccessDecision;
    data?: T;
    preview?: LinkableEntityPreview;
}
export interface RecipeReadResponse{
    access: AccessDecision;
    recipe?: Recipe;
    preview?: LinkableEntityPreview;
}

export type ContentResponse<T> = T | ContentReadResponse<T>;

export const isContentReadResponse = <T,>(value: unknown): value is ContentReadResponse<T> => {
    return typeof value === "object" && value !== null && "access" in value;
};
